import { rederiveMatchMinute } from '../clock';
import { MATCH_STATE } from '../machine';
import { ok, reject } from '../result';
import type {
  AdjustBoundariesCommand,
  AdjustBoundariesSnapshot,
  BoundaryOp,
  Decision,
  RederivableRow,
} from './types';
import type { MatchAnchors, MinuteRederivation } from '../../types';

/**
 * SES-8 — **boundary adjustment** (build unit U-14a).
 *
 * > SES-8: *"In triage, half boundaries are adjustable; stored `matchMinute` values re-derive through
 * > the single pure function of SES-10."*
 * >
 * > §10.1: *"The Triage screen carries an *Adjust half boundaries* control that edits `kickoffTs`,
 * > `halfTs`, `kickoff2Ts` and `fullTimeTs` and re-derives every Observation and MatchNote minute in
 * > one transaction. **It is rendered only when `Match.state ∈ {fullTime, triage, distill}`** and is
 * > absent when the Triage screen is reached from an in-match state."*
 *
 * ── The window is the requirement, and a rendering rule is not an enforcement ─
 *
 * §10.1 states the constraint as a *rendering* rule, and §17.1 records its negative as one too — *"the
 * control is **absent** when the Triage screen is reached from an in-match state"*. But §2.2 already
 * settled how this codebase reads that pairing: *"the UI additionally hides illegal controls, but the
 * domain rejection is the enforcement."* A control that is merely unrendered is one stale render, one
 * restored surface or one future caller away from firing, and §10.1's own sentence says what firing
 * costs — *"the most destructive control in the product … one mis-tap from the tile grid"*.
 *
 * So the window lives **here**, in the guard below, and the Triage screen's `absent` is the second
 * layer rather than the only one. `boundaries.test.ts` proves the negative against the handler — every
 * state but the three rejected, nothing emitted, swept from `MATCH_STATES` so a new state cannot slip
 * out of it — and `app/triageFlow.test.ts` proves through the real bus that a refusal leaves every
 * anchor and every minute untouched. The rendered half is asserted by `verify-locked-board.mjs`
 * (`data-boundaries="available"` in `triage`); its *absent* case needs the in-play entry point and is
 * U-14a-ii's.
 *
 * Note that this window and {@link handleResolveFlag}'s are **deliberately different sets**: a flag
 * may be resolved from the Triage screen during a dead spell (DIST-1), and at that moment
 * `Match.state` is `secondHalf` (§2.3 — Triage is a screen and changes no state). If both used one
 * window, one of the two requirements would be broken whichever set was chosen.
 *
 * ── Why the minutes are computed here and not in `data/` ────────────────────
 *
 * §2.4 gives re-derivation one expression, `deriveMatchMinute(newAnchors, stateAt(newAnchors,
 * createdAt), createdAt)`, wrapped as `clock.rederiveMatchMinute`. `repos/matches.writeAnchors`
 * explicitly declines to compute it (*"a second derivation path is how a second answer is born"*), and
 * the UI must not either, or §10.1's *"live preview of the resulting minute range before commit"*
 * would be a different calculation from the one that commits. {@link previewAdjustment} is therefore
 * exported from this module and is the same call the handler makes.
 */

const MODULE = 'commands/boundaries';

/**
 * §10.1 — the three states in which the anchors may be rewritten.
 *
 * Every one of them is post-`fullTime`: the match has stopped, so moving a boundary cannot change
 * what the live clock is currently showing anybody. That is the property the exclusion protects, and
 * it is why `halftime` is not a member even though it is a dead spell — a boundary moved during the
 * break moves the minute of a half that has not finished being played.
 */
const ADJUSTABLE_IN: ReadonlySet<string> = new Set<string>([
  MATCH_STATE.fullTime,
  MATCH_STATE.triage,
  MATCH_STATE.distill,
]);

/** The anchors in the order the wall-clock line puts them. Used by the ordering guard and its message. */
const ANCHOR_ORDER = ['kickoffTs', 'halfTs', 'kickoff2Ts', 'fullTimeTs'] as const;

type AnchorSet = Omit<MatchAnchors, 'halfLengthMin'>;

/** What an adjustment would do, without doing it. §10.1's *"live preview … before commit"*. */
export interface AdjustmentPreview {
  readonly observations: readonly MinuteRederivation[];
  readonly notes: readonly MinuteRederivation[];
  /** How many rows would actually move. Zero is a legitimate answer and is reported as one. */
  readonly changed: number;
  /** The resulting minute range across both tables, or `null` when there is nothing to derive. */
  readonly range: { readonly min: number; readonly max: number } | null;
}

/**
 * The re-derived minutes for one set of anchors — **the same call the handler commits**.
 *
 * Exported so the Triage screen's preview and the write cannot disagree. Pure and total: it reads
 * only `createdAt`, which §2.4 and INV-7's allowlist permit here precisely because `stateAt`
 * re-partitions the wall-clock line rather than treating `createdAt` as a record key.
 */
export function previewAdjustment(
  anchors: AnchorSet,
  /**
   * SES-5's half length, which the adjustment does **not** change and which the derivation cannot
   * do without: `deriveMatchMinute` returns `halfLengthMin` for a halftime row and
   * `halfLengthMin + elapsed` for a second-half one. A required parameter rather than a default,
   * because a default of `0` type-checks, runs, and quietly re-derives every second-half minute as
   * if the halves were zero-length — which is what the first version of this function did, and what
   * `boundaries.test.ts`'s fixture caught.
   */
  halfLengthMin: number,
  observations: readonly RederivableRow[],
  notes: readonly RederivableRow[],
): AdjustmentPreview {
  const full: MatchAnchors = { halfLengthMin, ...anchors };
  const derive = (rows: readonly RederivableRow[]): MinuteRederivation[] =>
    rows.map((row) => ({ id: row.id, matchMinute: rederiveMatchMinute(full, row.createdAt) }));

  const nextObservations = derive(observations);
  const nextNotes = derive(notes);
  const before = new Map<string, number>(
    [...observations, ...notes].map((row) => [row.id, row.matchMinute]),
  );
  const all = [...nextObservations, ...nextNotes];

  return {
    observations: nextObservations,
    notes: nextNotes,
    changed: all.filter((next) => before.get(next.id) !== next.matchMinute).length,
    range:
      all.length === 0
        ? null
        : {
            min: Math.min(...all.map((row) => row.matchMinute)),
            max: Math.max(...all.map((row) => row.matchMinute)),
          },
  };
}

/**
 * SES-8 — rewrite the four anchors and every minute derived from them, in one transaction.
 *
 * ── The guards, in the order of the questions ───────────────────────────────
 *
 * Does the match exist · is it in a state where the clock has stopped (§10.1's window, and §17.1's
 * negative) · are the anchors a *partition* rather than four numbers · does the adjustment erase an
 * anchor the match already has.
 *
 * The last is not pedantry. `stateAt` reads the anchors newest-first, so nulling `kickoff2Ts` on a
 * match that played a second half does not merely lose a boundary — it re-derives every second-half
 * observation as `halftime`, which SES-10 stamps at `halfLengthMin`. A control that could do that
 * silently would collapse an entire half onto one minute.
 */
export function handleAdjustBoundaries(
  cmd: AdjustBoundariesCommand,
  snap: AdjustBoundariesSnapshot,
): Decision<BoundaryOp> {
  const match = snap.match;
  if (match === undefined) {
    return reject(MODULE, cmd.matchId, 'ERR_MATCH_NOT_FOUND', 'That match no longer exists.');
  }

  if (!ADJUSTABLE_IN.has(match.state)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_BOUNDARIES_NOT_ADJUSTABLE',
      'Half boundaries can only be adjusted once the match has finished.',
      { state: match.state },
    );
  }

  const anchors = cmd.anchors;

  for (const field of ANCHOR_ORDER) {
    const value = anchors[field];
    if (value !== null && !Number.isFinite(value)) {
      return reject(MODULE, cmd.matchId, 'ERR_ANCHOR_INVALID', `${field} is not a real time.`, {
        field,
        value,
      });
    }
    if (value === null && match[field] !== null) {
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_ANCHOR_ERASED',
        'An adjustment can move a boundary but not remove one.',
        { field },
      );
    }
  }

  const ordered = ANCHOR_ORDER.map((field) => anchors[field]).filter(
    (value): value is number => value !== null,
  );
  for (let i = 1; i < ordered.length; i += 1) {
    // Indexed inside the loop bound, so both reads exist; `at()` would return `number | undefined`
    // and force a null check that says nothing about the requirement.
    const previous = ordered[i - 1] ?? 0;
    if ((ordered[i] ?? 0) < previous) {
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_ANCHOR_ORDER',
        'Kickoff, half time, second-half kickoff and full time must stay in that order.',
        { anchors: ordered },
      );
    }
  }

  const preview = previewAdjustment(anchors, match.halfLengthMin, snap.observations, snap.notes);

  return ok({
    // Anchors first: the re-derivation is *of* the new partition, and a reader inside this
    // transaction should never see minutes that no anchor on the row explains.
    ops: [
      { t: 'writeMatchAnchors', id: cmd.matchId, anchors },
      { t: 'rederiveObservationMinutes', updates: preview.observations },
      { t: 'rederiveNoteMinutes', updates: preview.notes },
    ],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg: 'SES-8: half boundaries adjusted',
        data: {
          from: {
            kickoffTs: match.kickoffTs,
            halfTs: match.halfTs,
            kickoff2Ts: match.kickoff2Ts,
            fullTimeTs: match.fullTimeTs,
          },
          to: anchors,
          observations: preview.observations.length,
          notes: preview.notes.length,
          minutesChanged: preview.changed,
          range: preview.range,
        },
      },
    ],
    // No postcondition. INV-10's `matchMinute ≥ 0` is asserted by both `rederiveMinutes` repo
    // functions inside this transaction, and `deriveMatchMinute` clamps at the source; INV-6 and
    // INV-5 concern rows this op set cannot compile.
    post: [],
  });
}
